/**
 * The dialog's editable recurrence model — moved from the client calendar
 * arche (`utils.ts` / `types.ts`, which now re-export this module).
 *
 * Design constraint carried over intact: a stored rule must survive the
 * open → save round-trip BYTE-IDENTICALLY when the user changed nothing.
 * rrule.js cannot provide that (it re-serialises in its own order and
 * expands defaults), so the model keeps the verbatim-carry design: the
 * builder models FREQ, INTERVAL, COUNT, UNTIL, WKST and — for WEEKLY only —
 * BYDAY; everything else rides through untouched (`carriedParts`,
 * `untilRaw`, `weekStart`), and two additions generalise it further:
 *
 *  - `envelope`: the non-RRULE lines of a multi-line stored value
 *    (`DTSTART:…`, `EXDATE:…`), carried verbatim in order. Previously a
 *    multi-line value fell through the parser's FREQ lookup and silently
 *    became bare `FREQ=WEEKLY` — the dialog REWROTE a rule it only read.
 *  - `opaque`: a rule whose FREQ the builder does not model (sub-daily from
 *    another CalDAV client, or an unknown token) is carried WHOLE and
 *    rendered read-only — never silently rewritten to weekly, which is what
 *    the old `freqMap[…] || 'weekly'` fallback did.
 */

import { parseRecurrenceText } from './text';
import { fromFloating } from './zone';

export interface EditableRecurrenceRule {
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number;
  byDay?: string[];
  endType: 'never' | 'count' | 'until';
  count?: number;
  until?: string;
  /**
   * Week start, carried through verbatim.
   *
   * The builder has no control for it, but the rules that arrive from Apple
   * and Outlook nearly all carry `WKST=SU`. Dropping it on parse meant every
   * round-trip through this dialog silently rewrote the stored rule —
   * visible to the user as a refusal to save one occurrence because "the
   * repeat rule" had changed, when nothing had.
   */
  weekStart?: string;
  /**
   * The UNTIL token exactly as it arrived, when the builder parsed one.
   *
   * The builder models UNTIL as a DATE, but providers write a full instant
   * and they do not agree on the time of day (Apple emits 05:59:59Z where
   * the builder would write 23:59:59Z). Re-emitting the original when the
   * date has not changed keeps a save from moving the series end — and
   * keeps a round-trip from looking like an edit.
   */
  untilRaw?: string;
  /**
   * Every rule part the builder does NOT model, carried through verbatim.
   *
   * Same argument as `weekStart` and `untilRaw`, generalised. Without this,
   * `FREQ=MONTHLY;BYDAY=3TU` came back as bare `FREQ=MONTHLY`: opening a
   * "third Tuesday" meeting to fix a typo re-anchored the series to the
   * master's day-of-month and pushed that to the provider. BYMONTHDAY,
   * BYSETPOS, BYMONTH and any non-weekly BYDAY all did this.
   *
   * Paired with `carriedFrequency` so they are dropped when the user
   * actually changes the frequency — a BYMONTHDAY belongs to the MONTHLY
   * rule it was read from, not to the WEEKLY one that replaced it.
   */
  carriedParts?: Array<[string, string]>;
  /** The FREQ these `carriedParts` were read from. See above. */
  carriedFrequency?: EditableRecurrenceRule['frequency'];
  /**
   * Multi-line stored values: the lines around the RRULE line, verbatim and
   * in order, so the rebuild reproduces the whole component byte-for-byte.
   */
  envelope?: { linesBefore: string[]; linesAfter: string[] };
  /**
   * A rule the builder cannot model (unknown or sub-daily FREQ, or an
   * unparseable value): the ENTIRE stored text, carried whole. When set,
   * `buildRruleString` returns it verbatim and every other field is
   * display-only default. The UI renders such rules read-only.
   */
  opaque?: { text: string };
}

const FREQUENCY_LABELS: Record<string, string> = {
  daily: 'Day',
  weekly: 'Week',
  monthly: 'Month',
  yearly: 'Year',
};

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function buildRecurrenceLabel(rule: EditableRecurrenceRule): string {
  if (rule.opaque) return 'Custom repeat rule';

  const freqLabel = FREQUENCY_LABELS[rule.frequency] || rule.frequency;
  let label = rule.interval > 1 ? `Every ${rule.interval} ${freqLabel}s` : `Every ${freqLabel}`;

  if (rule.frequency === 'weekly' && rule.byDay && rule.byDay.length > 0) {
    label += ` on ${rule.byDay.join(', ')}`;
  }

  if (rule.endType === 'count' && rule.count) {
    label += `, ${rule.count} times`;
  } else if (rule.endType === 'until' && rule.until) {
    const untilDate = new Date(rule.until);
    label += `, until ${MONTH_NAMES[untilDate.getMonth()]} ${untilDate.getDate()}, ${untilDate.getFullYear()}`;
  }

  return label;
}

const FREQ_TO_MODEL: Record<string, EditableRecurrenceRule['frequency']> = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
};

/**
 * Rule parts the builder actually has a control for. Everything else is
 * carried through verbatim — see `EditableRecurrenceRule.carriedParts`.
 */
const MODELED_RRULE_PARTS = new Set(['FREQ', 'INTERVAL', 'COUNT', 'UNTIL', 'WKST']);

function opaqueRule(text: string): EditableRecurrenceRule {
  return { frequency: 'weekly', interval: 1, endType: 'never', opaque: { text } };
}

export function parseRruleToRule(rruleStr: string | null): EditableRecurrenceRule | null {
  if (!rruleStr) return null;

  const parsedText = parseRecurrenceText(rruleStr);
  // Unparseable values (or ones carrying constructs like a duplicate RRULE)
  // are carried whole rather than guessed at.
  if (!parsedText) return opaqueRule(rruleStr);

  const envelope = parsedText.isMultiLine
    ? { linesBefore: parsedText.linesBeforeRrule, linesAfter: parsedText.linesAfterRrule }
    : undefined;

  const parts = new Map<string, string>();
  for (const part of parsedText.rrule.split(';')) {
    const [key, val] = part.split('=');
    if (key && val) parts.set(key.toUpperCase(), val);
  }

  const frequency = FREQ_TO_MODEL[(parts.get('FREQ') || '').toUpperCase()];
  // A FREQ the builder has no model for (sub-daily, bogus, absent): carry
  // the whole text opaquely — the old `|| 'weekly'` fallback silently
  // rewrote such rules on the next save.
  if (!frequency) return opaqueRule(rruleStr);

  const interval = parseInt(parts.get('INTERVAL') || '1', 10);

  // WEEKLY only. `byDay` is the day-checkbox model, and those render plain
  // weekday tokens — an ordinal like '3TU' belongs to a MONTHLY/YEARLY rule
  // and rides in `carriedParts` instead. Populating it here regardless meant
  // opening a `FREQ=MONTHLY;BYDAY=3TU` event and switching Repeat to Weekly
  // carried '3TU' forward, the checkboxes rendered with nothing selected,
  // and saving emitted `FREQ=WEEKLY;BYDAY=3TU` — invalid per RFC 5545 and
  // rejected by Graph.
  const byDay = frequency === 'weekly' ? parts.get('BYDAY')?.split(',') : undefined;

  let endType: EditableRecurrenceRule['endType'] = 'never';
  let count: number | undefined;
  let until: string | undefined;

  if (parts.has('COUNT')) {
    endType = 'count';
    count = parseInt(parts.get('COUNT')!, 10);
  } else if (parts.has('UNTIL')) {
    endType = 'until';
    const untilStr = parts.get('UNTIL')!;
    // Parse YYYYMMDDTHHMMSSZ format
    const match = untilStr.match(/^(\d{4})(\d{2})(\d{2})/);
    if (match) {
      until = `${match[1]}-${match[2]}-${match[3]}`;
    }
  }

  // Everything the builder does not model, kept verbatim. Without this the
  // dialog REWRITES the rule it was only meant to read.
  const carriedParts: Array<[string, string]> = [];
  for (const [key, value] of parts) {
    if (MODELED_RRULE_PARTS.has(key)) continue;
    // BYDAY is modelled by the day checkboxes for a WEEKLY rule only. On any
    // other frequency it means something the builder has no control for
    // ('3TU' = the third Tuesday), so it has to ride along.
    if (key === 'BYDAY' && frequency === 'weekly') continue;
    carriedParts.push([key, value]);
  }

  return {
    frequency,
    interval,
    byDay,
    endType,
    count,
    until,
    weekStart: parts.get('WKST'),
    untilRaw: parts.get('UNTIL'),
    ...(carriedParts.length > 0 ? { carriedParts, carriedFrequency: frequency } : {}),
    ...(envelope ? { envelope } : {}),
  };
}

export interface BuildRruleOptions {
  /**
   * The series' anchor zone, used ONLY when synthesising an UNTIL instant
   * for a user-picked end date: "repeats until Sep 1" means through the end
   * of Sep 1 IN THE SERIES ZONE. Without it the historical UTC end-of-day
   * (`T235959Z`) is kept — correct for UTC-anchored series and the exact
   * legacy output everywhere else.
   */
  zone?: string;
}

export function buildRruleString(
  rule: EditableRecurrenceRule,
  options: BuildRruleOptions = {},
): string {
  // A rule the builder could not model round-trips verbatim, whole.
  if (rule.opaque) return rule.opaque.text;

  const freqMap: Record<string, string> = {
    daily: 'DAILY',
    weekly: 'WEEKLY',
    monthly: 'MONTHLY',
    yearly: 'YEARLY',
  };

  // The bare RRULE VALUE, with no `RRULE:` property prefix. That prefix is
  // the iCalendar property name and belongs only in a serialized .ics line —
  // the vault column stores the value. Provider sync stores the bare form
  // (inbound mappers strip the prefix), so writing the prefixed form here
  // made a single calendar hold both, and pushing such an event produced
  // `RRULE:RRULE:FREQ=…` which Google rejects with 400. `parseRruleToRule`
  // accepts either form, so events already stored with the prefix keep
  // reading correctly.
  let rrule = `FREQ=${freqMap[rule.frequency]}`;

  if (rule.interval > 1) {
    rrule += `;INTERVAL=${rule.interval}`;
  }

  if (rule.frequency === 'weekly' && rule.byDay && rule.byDay.length > 0) {
    rrule += `;BYDAY=${rule.byDay.join(',')}`;
  }

  // Re-emitted only because they were there — the same reason WKST and the
  // UNTIL token below are. Dropped when the user CHANGED the frequency: a
  // BYMONTHDAY read off a MONTHLY rule is meaningless on the WEEKLY rule
  // that replaced it, and carrying it would build something the user never
  // asked for.
  if (rule.carriedParts && rule.carriedFrequency === rule.frequency) {
    for (const [key, value] of rule.carriedParts) {
      rrule += `;${key}=${value}`;
    }
  }

  // Emitted only because it was there. The builder offers no control for
  // week start, so re-emitting what the rule already carried is the
  // difference between reading a rule and REWRITING it — and rules from
  // Apple and Outlook nearly all carry WKST=SU.
  if (rule.weekStart) {
    rrule += `;WKST=${rule.weekStart}`;
  }

  if (rule.endType === 'count' && rule.count) {
    rrule += `;COUNT=${rule.count}`;
  } else if (rule.endType === 'until' && rule.until) {
    // `rule.until` is already a plain 'YYYY-MM-DD'. Round-tripping it through
    // Date lost a day for everyone west of UTC: `new Date('2032-03-26')` is
    // UTC midnight, and reading it back with LOCAL getters gives the 25th.
    // That silently truncated the series by a day on every save.
    // Re-emit the original token when the DATE is unchanged: the builder has
    // no time-of-day control, so synthesising an instant over a provider's
    // 05:59:59Z would move the series end on a save that changed nothing.
    // `rule.until` may be a plain 'YYYY-MM-DD' (from parseRruleToRule) or a
    // full ISO datetime (from the builder's own input), so take the date
    // part of either rather than assuming one shape.
    const asDate = rule.until.slice(0, 10).replace(/-/g, '');
    const untilStr =
      rule.untilRaw && rule.untilRaw.startsWith(asDate)
        ? rule.untilRaw
        : synthesizeUntil(rule.until.slice(0, 10), options.zone);
    rrule += `;UNTIL=${untilStr}`;
  }

  if (rule.envelope) {
    return [...rule.envelope.linesBefore, `RRULE:${rrule}`, ...rule.envelope.linesAfter].join('\n');
  }

  return rrule;
}

/**
 * A user-picked end DATE becomes an explicit UTC DATE-TIME UNTIL meaning
 * "through the end of that day in the series zone". Historical output
 * (`T235959Z`, i.e. UTC end-of-day) is kept when no zone is supplied or the
 * zone has no offset to apply.
 */
function synthesizeUntil(dateIso: string, zone: string | undefined): string {
  const compactDate = dateIso.replace(/-/g, '');
  if (!zone || zone === 'UTC' || zone === 'Etc/UTC' || zone === 'FLOATING') {
    return `${compactDate}T235959Z`;
  }
  const [y, m, d] = dateIso.split('-').map(Number);
  const wallEndOfDay = new Date(Date.UTC(y, m - 1, d, 23, 59, 59));
  const instant = fromFloating(wallEndOfDay, zone);
  return instant
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
}
